import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {collection, onSnapshot, query, where} from 'firebase/firestore';
import {db} from '../config/firebase';
import Layout from '../components/Layout';
import {useAuth} from '../context/AuthContext';
import {useCart} from '../context/CartContext';
import {useMerchant} from '../context/MerchantContext';
import {Order, OrderStatus, PageOrderDto} from '../types';
import {orderApi, restaurantApi, reviewApi} from '../services/api';
import toast from 'react-hot-toast';
import {
  CloseButton,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle
} from '../components/common/modal/ModalComponents';

const StatusBadge = styled.div<{ status: string }>`
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  background-color: ${({ status }) => {
    switch (status) {
      case 'RECEIVED':
        return '#f39c12';
      case 'PROCESSING':
        return '#3498db';
      case 'CREATED':
        return '#7f8c8d';
      case 'CONFIRMED':
        return '#3498db';
      case 'ACCEPTED':
        return '#9b59b6';
      case 'READY_FOR_TAKEAWAY':
        return '#2ecc71';
      case 'DELIVERED':
        return '#27ae60';
      case 'CANCELLED':
      case 'CANCELLED_BY_TIMEOUT':
      case 'CANCELLED_BY_MERCHANT':
      case 'CANCELLED_BY_CUSTOMER':
      case 'CANCELLED_BY_CHECK_AVAILABILITY':
        return '#e74c3c';
      case 'ERROR':
        return '#c0392b';
      default:
        return '#95a5a6';
    }
  }};
  color: white;
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;

  @media (max-width: 576px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const PaginationActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const PageSizeControl = styled.label<{ $themeColor: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 500;

  select {
    border: 1px solid #dee2e6;
    border-radius: 6px;
    color: #495057;
    background: #fff;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 0.35rem 2rem 0.35rem 0.65rem;
    cursor: pointer;

    &:focus {
      border-color: ${props => props.$themeColor};
      box-shadow: 0 0 0 0.15rem ${props => props.$themeColor}33;
      outline: none;
    }
  }
`;

const PaginationButton = styled.button<{ $active?: boolean; $themeColor: string }>`
  min-width: 2.25rem;
  border: 1px solid ${props => props.$active ? props.$themeColor : '#dee2e6'};
  border-radius: 6px;
  background: ${props => props.$active ? props.$themeColor : '#fff'};
  color: ${props => props.$active ? '#fff' : '#495057'};
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.35rem 0.65rem;
  cursor: pointer;

  &:disabled {
    color: ${props => props.$active ? '#fff' : '#adb5bd'};
    cursor: not-allowed;
    background: ${props => props.$active ? props.$themeColor : '#f8f9fa'};
  }

  &:not(:disabled):hover {
    border-color: ${props => props.$themeColor};
    color: ${props => props.$active ? '#fff' : props.$themeColor};
  }
`;

const SkeletonBlock = styled.div<{ width?: string; height?: string }>`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '1rem'};
  border-radius: 6px;
  background: linear-gradient(90deg, #edf2f7 25%, #f8fafc 37%, #edf2f7 63%);
  background-size: 400% 100%;
  animation: skeleton-loading 1.4s ease infinite;

  @keyframes skeleton-loading {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }
`;

const ReviewIntro = styled.div`
  display: grid;
  gap: 0.35rem;
  text-align: center;
  color: #4a5568;

  strong {
    color: #2d3748;
    font-size: 1.05rem;
  }
`;

const StarsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.45rem;
  margin: 1rem 0;
`;

const StarButton = styled.button<{ $active: boolean }>`
  height: 54px;
  border: 1px solid ${props => props.$active ? '#f59e0b' : '#e2e8f0'};
  background: ${props => props.$active ? '#fffbeb' : '#fff'};
  color: ${props => props.$active ? '#d97706' : '#94a3b8'};
  border-radius: 8px;
  font-size: 1.7rem;
  cursor: pointer;

  &:hover {
    border-color: #f59e0b;
    color: #d97706;
  }
`;

const ReviewTextArea = styled.textarea`
  width: 100%;
  min-height: 112px;
  resize: vertical;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0.75rem;
  font: inherit;
  box-sizing: border-box;

  &:focus {
    border-color: #f59e0b;
    box-shadow: 0 0 0 0.15rem rgba(245, 158, 11, 0.18);
    outline: none;
  }
`;

const SubmitReviewButton = styled.button`
  width: 100%;
  min-height: 44px;
  border: none;
  border-radius: 8px;
  background: #f59e0b;
  color: white;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryReviewButton = styled.button`
  width: 100%;
  min-height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #4a5568;
  font-weight: 700;
  cursor: pointer;
`;

const DEFAULT_ORDERS_PAGE_SIZE = 9;
const ORDERS_PAGE_SIZE_OPTIONS = [9, 18, 27];

const OrdersPage: React.FC = () => {
  const { user, isAuthenticated, setShowLoginModal } = useAuth();
  const { addItem } = useCart();
  const { 
    merchantId, 
    restaurant, 
    setIsCheckoutModalOpen, 
    setSelectedOrder, 
    selectedOrder,
    setMenuCategories 
  } = useMerchant();
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersPage, setOrdersPage] = useState<PageOrderDto | null>(null);
  const [currentOrdersPage, setCurrentOrdersPage] = useState(0);
  const [ordersPageSize, setOrdersPageSize] = useState(DEFAULT_ORDERS_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [reviewOrder, setReviewOrder] = useState<Order | null>(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // If not authenticated, show login modal
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    // Fetch orders for the authenticated user via REST API
    fetchOrders();
  }, [isAuthenticated, currentOrdersPage, ordersPageSize]);

  useEffect(() => {
    // Listen to real-time updates from Firebase (Firestore)
    if (isAuthenticated && user?.id && db) {
      const ordersRef = collection(db, 'orders');
      // Filter by customer id - assuming it's stored in a field called 'customerId' or 'customer.id'
      // Adjust the field name based on how your backend stores it in Firestore
      const q = query(ordersRef, where('customerId', '==', user.id));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const changes = snapshot.docChanges();
        
        // 1. Atualiza o estado imediatamente com os dados do Firebase
        setOrders((prevOrders) => {
          let newOrders = [...prevOrders];
          let hasChanges = false;

          changes.forEach((change) => {
            const updatedOrder = { ...change.doc.data(), id: change.doc.id } as Order;
            
            if (change.type === 'modified' || change.type === 'added') {
              const index = newOrders.findIndex(o => o.id === updatedOrder.id);
              if (index !== -1) {
                newOrders[index] = { ...newOrders[index], ...updatedOrder };
              } else if (currentOrdersPage === 0 && change.type === 'added') {
                newOrders = [updatedOrder, ...newOrders];
              } else {
                return;
              }
              hasChanges = true;
              
              if (selectedOrder && selectedOrder.id === updatedOrder.id) {
                const updatedItem = index !== -1 ? newOrders[index] : newOrders[0];
                setSelectedOrder(updatedItem);
              }
            } else if (change.type === 'removed') {
              newOrders = newOrders.filter(o => o.id !== updatedOrder.id);
              hasChanges = true;
            }
          });

          if (hasChanges) {
            return newOrders.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          }
          return prevOrders;
        });

        // 2. Para status específicos, confirma com o backend para obter dados completos
        changes.forEach(async (change) => {
          const data = change.doc.data();
          const id = change.doc.id;
          const statusToConfirm = ['ACCEPTED', 'CONFIRMED', 'CREATED', 'RECEIVED', 'DELIVERED'];
          
          if ((change.type === 'added' || change.type === 'modified') && statusToConfirm.includes(data.status)) {
            try {
              const fullOrder = await orderApi.getOrder(data.merchantId || merchantId || '', id);
              setOrders(prev => {
                const index = prev.findIndex(o => o.id === fullOrder.id);
                if (index === -1) return prev;
                const newOrders = [...prev];
                newOrders[index] = { ...fullOrder, status: data.status };
                
                if (selectedOrder && selectedOrder.id === fullOrder.id) {
                  setSelectedOrder({ ...fullOrder, status: data.status });
                }
                return newOrders;
              });
            } catch (err) {
              console.error('Error confirming order from backend:', err);
            }
          }
        });
      }, (error) => {
        console.error('Firebase onSnapshot error:', error);
      });

      return () => unsubscribe();
    }
  }, [isAuthenticated, user?.id, merchantId, currentOrdersPage, setSelectedOrder, selectedOrder]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await orderApi.listOrders(currentOrdersPage, ordersPageSize);
      // Sort orders by createdAt descending (newest first)
      const sortedData = [...data.content].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setOrders(sortedData);
      setOrdersPage(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRepeatOrder = async (order: Order) => {
    if (!merchantId) return;
    try {
      // Get all current menu items to make sure they still exist and have correct info
      const categories = await restaurantApi.getMenu(merchantId);
      setMenuCategories(categories);
      const menuItems = categories.flatMap(cat => cat.items);
      
      // Clear the current cart before repeating the order
      // clearCart(); // Removed: user might want to keep current items

      let itemsAdded = 0;
      for (const orderItem of order.items) {
        const menuItem = menuItems.find(mi => mi.id === orderItem.menuItemId);
        if (menuItem && menuItem.available) {
          // Reconstruct options if they exist
          addItem(menuItem, orderItem.quantity, orderItem.options, orderItem.notes);
          itemsAdded++;
        }
      }

      if (itemsAdded > 0) {
        navigate('/'); // Redirect to menu instead of cart
      } else {
        alert('Desculpe, os itens deste pedido não estão mais disponíveis.');
      }
    } catch (err) {
      console.error('Error repeating order:', err);
      alert('Ocorreu um erro ao tentar repetir o pedido.');
    }
  };

  const handleLoginSuccess = () => {
    // Fetch orders after successful login
    fetchOrders();
  };

  const showOrdersPagination = !!ordersPage && ordersPage.totalPages > 1;
  const themeColor = restaurant?.theme?.primary || '#0d6efd';
  const getVisiblePages = (currentPage: number, totalPages: number) => {
    const maxVisible = 5;
    const half = Math.floor(maxVisible / 2);
    let start = Math.max(0, currentPage - half);
    let end = Math.min(totalPages - 1, start + maxVisible - 1);

    start = Math.max(0, end - maxVisible + 1);

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };

  const renderOrderSkeletons = () => (
    <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
      {Array.from({ length: DEFAULT_ORDERS_PAGE_SIZE }).map((_, index) => (
        <div key={index} className="col">
          <div className="card h-100 shadow-sm border-0 overflow-hidden">
            <div className="card-header bg-white border-bottom py-2 px-3">
              <div className="d-flex justify-content-between align-items-center">
                <div style={{ flex: 1 }}>
                  <SkeletonBlock width="65%" height="1rem" />
                  <div style={{ marginTop: '0.5rem' }}>
                    <SkeletonBlock width="42%" height="0.75rem" />
                  </div>
                </div>
                <SkeletonBlock width="76px" height="24px" />
              </div>
            </div>
            <div className="card-body px-3 py-2">
              <div style={{ display: 'grid', gap: '0.55rem' }}>
                <SkeletonBlock width="100%" height="0.85rem" />
                <SkeletonBlock width="86%" height="0.85rem" />
                <SkeletonBlock width="72%" height="0.85rem" />
              </div>
              <div className="d-flex justify-content-between align-items-center pt-3 mt-2">
                <SkeletonBlock width="50px" height="0.85rem" />
                <SkeletonBlock width="82px" height="1.1rem" />
              </div>
            </div>
            <div className="card-footer bg-light border-top-0 px-3 py-2 text-end">
              <SkeletonBlock width="76px" height="31px" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PROCESSING:
        return 'Processando';
      case OrderStatus.CREATED:
        return 'Pendente';
      case OrderStatus.RECEIVED:
        return 'Pendente';
      case OrderStatus.CONFIRMED:
        return 'Confirmado';
      case OrderStatus.ACCEPTED:
        return 'Em Preparo';
      case OrderStatus.READY_FOR_TAKEAWAY:
        return 'Pronto';
      case OrderStatus.READY_FOR_DELIVERY:
        return 'Em Preparo';
      case OrderStatus.OUT_FOR_DELIVERY:
        return 'Saiu para Entrega';
      case OrderStatus.DELIVERED:
        return 'Entregue';
      case OrderStatus.CANCELLED:
        return 'Cancelado';
      case OrderStatus.CANCELLED_BY_CHECK_AVAILABILITY:
        return 'Cancelado por indisponibilidade';
      case OrderStatus.CANCELLED_BY_TIMEOUT:
        return 'Tempo esgotado';
      case OrderStatus.CANCELLED_BY_MERCHANT:
        return 'Cancelado pela loja';
      case OrderStatus.CANCELLED_BY_CUSTOMER:
        return 'Cancelado pelo cliente';
      case OrderStatus.ERROR:
        return 'Erro';
      default:
        return status;
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setIsCheckoutModalOpen(true);
  };

  const canReviewOrder = (order: Order) =>
    order.status === OrderStatus.DELIVERED &&
    !!order.reviewInvitation?.token &&
    !order.reviewInvitation.alreadyReviewed &&
    !order.reviewInvitation.expired;

  const handleReviewOrder = (order: Order) => {
    if (!order.reviewInvitation?.token) return;
    setReviewOrder(order);
    setReviewRating(0);
    setReviewComment('');
  };

  const closeReviewModal = () => {
    if (submittingReview) return;
    setReviewOrder(null);
    setReviewRating(0);
    setReviewComment('');
  };

  const submitReview = async () => {
    if (!reviewOrder?.reviewInvitation?.token || !reviewRating) {
      toast.error('Escolha uma nota para continuar');
      return;
    }

    setSubmittingReview(true);
    try {
      await reviewApi.submit({
        token: reviewOrder.reviewInvitation.token,
        rating: reviewRating,
        comment: reviewComment.trim() || undefined,
      });

      const markReviewed = (order: Order): Order => order.id === reviewOrder.id && order.reviewInvitation
        ? {
            ...order,
            reviewInvitation: {
              ...order.reviewInvitation,
              alreadyReviewed: true,
              status: 'REVIEWED',
              token: undefined,
            }
          }
        : order;

      setOrders(prev => prev.map(markReviewed));
      setOrdersPage(prev => prev ? { ...prev, content: prev.content.map(markReviewed) } : prev);
      if (selectedOrder?.id === reviewOrder.id) {
        setSelectedOrder(markReviewed(selectedOrder));
      }

      toast.success('Avaliação enviada');
      setReviewOrder(null);
      setReviewRating(0);
      setReviewComment('');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao enviar avaliação');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setOrdersPageSize(Number(event.target.value));
    setCurrentOrdersPage(0);
  };

  return (
    <Layout>
      <div className="py-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
          <h1 className="display-5 fw-bold mb-0">Meus Pedidos</h1>
          <PageSizeControl $themeColor={themeColor}>
            Pedidos por página
            <select value={ordersPageSize} onChange={handlePageSizeChange} disabled={loading}>
              {ORDERS_PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </PageSizeControl>
        </div>

        {loading ? (
          renderOrderSkeletons()
        ) : orders.length > 0 ? (
          <>
            <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
              {orders.map(order => (
                <div key={order.id} className="col">
                  <div className="card h-100 shadow-sm border-0 overflow-hidden" style={{ cursor: 'pointer' }} onClick={() => handleOrderClick(order)}>
                    <div className="card-header bg-white border-bottom py-2 px-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="min-w-0">
                          <h3 className="h6 mb-0 text-primary fw-bold text-truncate">Pedido #{order.orderDisplayId || order.id.split('-')[1]}</h3>
                          <span className="small text-muted" style={{ fontSize: '0.75rem' }}>{formatDate(order.createdAt)}</span>
                        </div>
                        <StatusBadge status={order.status} className="badge ms-2" style={{ padding: '0.3rem 0.6rem', fontSize: '0.7rem' }}>{getStatusText(order.status)}</StatusBadge>
                      </div>
                    </div>

                    <div className="card-body px-3 py-2">
                      <div className="mb-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                        {order.items.map((item, index) => (
                          <div key={index} className="d-flex justify-content-between align-items-center py-1 border-bottom-dashed">
                            <span className="small text-truncate me-2" style={{ fontSize: '0.8rem' }}>{item.quantity}x {item.name}</span>
                            <span className="small fw-bold" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{formatCurrency(item.price * item.quantity)}</span>
                          </div>
                        ))}
                      </div>

                      {order.cancellationReasonDescription && (
                        <div className="text-danger small mb-2" style={{ fontSize: '0.75rem', fontWeight: '500' }}>
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {order.cancellationReasonDescription}
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center pt-1 mt-auto">
                        <span className="small mb-0 fw-bold">Total</span>
                        <span className="h6 mb-0 fw-bold text-dark">{formatCurrency(order.payments && order.payments.length > 0 ? order.payments.reduce((sum, p) => sum + p.amount, 0) : order.total)}</span>
                      </div>
                    </div>

                    <div className="card-footer bg-light border-top-0 px-3 py-2">
                      <div className="d-flex justify-content-end gap-2 flex-wrap">
                        {canReviewOrder(order) && (
                          <button className="btn btn-warning btn-sm px-3 fw-bold text-white" style={{ fontSize: '0.75rem' }} onClick={(e) => {
                            e.stopPropagation();
                            handleReviewOrder(order);
                          }}>
                            Avaliar
                          </button>
                        )}
                        <button className="btn btn-primary btn-sm px-3 fw-bold" style={{ fontSize: '0.75rem' }} onClick={(e) => {
                          e.stopPropagation();
                          handleRepeatOrder(order);
                        }}>
                          Repetir
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {showOrdersPagination && (
              <PaginationBar>
                <span className="text-muted small">
                  Página {(ordersPage?.number ?? 0) + 1} de {ordersPage?.totalPages}
                </span>
                <PaginationActions>
                  <PaginationButton
                    type="button"
                    $themeColor={themeColor}
                    disabled={loading || ordersPage?.first}
                    onClick={() => setCurrentOrdersPage(0)}
                  >
                    Primeira
                  </PaginationButton>
                  <PaginationButton
                    type="button"
                    $themeColor={themeColor}
                    disabled={loading || ordersPage?.first}
                    onClick={() => setCurrentOrdersPage(page => Math.max(0, page - 1))}
                  >
                    Anterior
                  </PaginationButton>
                  {getVisiblePages(ordersPage?.number ?? 0, ordersPage?.totalPages ?? 0).map(page => (
                    <PaginationButton
                      key={page}
                      type="button"
                      $themeColor={themeColor}
                      $active={page === ordersPage?.number}
                      disabled={loading || page === ordersPage?.number}
                      onClick={() => setCurrentOrdersPage(page)}
                    >
                      {page + 1}
                    </PaginationButton>
                  ))}
                  <PaginationButton
                    type="button"
                    $themeColor={themeColor}
                    disabled={loading || ordersPage?.last}
                    onClick={() => setCurrentOrdersPage(page => page + 1)}
                  >
                    Próxima
                  </PaginationButton>
                  <PaginationButton
                    type="button"
                    $themeColor={themeColor}
                    disabled={loading || ordersPage?.last}
                    onClick={() => setCurrentOrdersPage(Math.max(0, (ordersPage?.totalPages ?? 1) - 1))}
                  >
                    Última
                  </PaginationButton>
                </PaginationActions>
              </PaginationBar>
            )}
          </>
        ) : (
          <div className="text-center py-5 bg-light rounded-3 shadow-sm">
            <i className="fas fa-history mb-4" style={{ fontSize: '4rem', color: '#dee2e6' }}></i>
            <h2 className="h4 mb-3">Você ainda não fez nenhum pedido.</h2>
            <p className="text-muted mb-4">Adicione itens ao carrinho para começar.</p>
            <button className="btn btn-primary btn-lg px-5" onClick={() => navigate('/')}>
              Ver Cardápio
            </button>
          </div>
        )}
      </div>
      {reviewOrder && (
        <ModalOverlay onClick={closeReviewModal} zIndex={10050}>
          <ModalContent onClick={event => event.stopPropagation()} maxWidth="480px">
            <ModalHeader sticky noBorder>
              <div style={{ width: 32 }} />
              <ModalTitle>Avaliar Pedido</ModalTitle>
              <CloseButton onClick={closeReviewModal} disabled={submittingReview} />
            </ModalHeader>
            <ModalBody>
              <ReviewIntro>
                <strong>Pedido #{reviewOrder.orderDisplayId || reviewOrder.id.split('-')[1]}</strong>
                <span>Como foi sua experiência?</span>
              </ReviewIntro>

              <StarsRow>
                {[1, 2, 3, 4, 5].map(value => (
                  <StarButton
                    key={value}
                    type="button"
                    $active={value <= reviewRating}
                    onClick={() => setReviewRating(value)}
                    aria-label={`${value} estrelas`}
                  >
                    ★
                  </StarButton>
                ))}
              </StarsRow>

              <label className="form-label fw-bold text-dark">Comentário opcional</label>
              <ReviewTextArea
                value={reviewComment}
                onChange={event => setReviewComment(event.target.value)}
                maxLength={800}
                placeholder="Conte como foi seu pedido"
                disabled={submittingReview}
              />
            </ModalBody>
            <ModalFooter sticky>
              <SubmitReviewButton type="button" disabled={submittingReview || !reviewRating} onClick={submitReview}>
                {submittingReview ? 'Enviando...' : 'Enviar avaliação'}
              </SubmitReviewButton>
              <SecondaryReviewButton type="button" disabled={submittingReview} onClick={closeReviewModal}>
                Agora não
              </SecondaryReviewButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default OrdersPage;
